import type { AnalysisClient } from './BatchAnalysis'

/**
 * Reason codes from the funding engine and the ViewThru screening gates, with
 * the wording shown on the dashboard. Shared by the analysis panel, the client
 * modal and the all-batches page so every surface names a failure the same way.
 */
export const CLIENT_REASON_LABELS: Record<string, string> = {
    clientsWithCcjOrVA: 'CCJ or voluntary arrangement',
    clientsExcludedMissingCfa: 'Missing CFA / signature date',
    clientsExcludedMissingCreditReportDocument: 'Missing credit report PDF',
    clientsBelowMinimum: 'Below minimum client value',
    clientsAllClaimsRejected: 'All agreements rejected',
    clientsWithDuplicateEmail: 'Duplicate email',
    clientsExcludedMissingAmlDocument: 'Missing AML report',
    clientsExcludedIdentityCheckFailed: 'Identity check failed or missing',
    clientsExcludedPreviousIndustrySearch: 'Previous industry search',
    clientsExcludedDeceased: 'Recorded as deceased',
    clientsWithBankruptcy: 'Bankruptcy on record',
    clientsWithNoPreLatestDateValue: 'No agreements before October 2024',
}

export const CLAIM_REASON_LABELS: Record<string, string> = {
    claimsExcludedDuplicatePolicyNumber: 'Duplicate agreement number',
    claimsExcludedPre2007: 'Agreement started before 2007',
    claimsExcludedAfterLatestStartDate: 'Agreement started October 2024 or later',
    claimsExcludedClaimantUnder18: 'Client under 18 at agreement start',
    claimsExcludedAgreementStatus: 'Agreement status not accepted',
    claimsExcludedBelowMinLoan: 'Starting balance below £5,000',
    claimsExcludedUnapprovedLender: 'Unapproved lender',
    claimsExcludedNoLengthOfContract: 'Missing contract term',
    claimsExcludedBelowMinContractLength: 'Contract term under 5 months',
    claimsExcludedBelowMinMonthlyRepayment: 'Monthly payment below £50',
    claimsExcludedExcessiveMonthlyRepayment: 'Monthly payment above £1,500',
}

export const reasonLabel = (reason: string | null) => {
    if (!reason) return 'Eligible'
    if (CLIENT_REASON_LABELS[reason]) return CLIENT_REASON_LABELS[reason]
    if (CLAIM_REASON_LABELS[reason]) return CLAIM_REASON_LABELS[reason]
    if (reason.startsWith('claimsExcludedQuantum:')) {
        const detail = reason.split(':')[1]?.replaceAll('_', ' ') || 'unknown'
        return `Valuation failed: ${detail}`
    }
    return reason.replaceAll('_', ' ')
}

export const money = (value: number | null | undefined) =>
    typeof value === 'number' && Number.isFinite(value)
        ? value.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })
        : '—'

export const shortDate = (value: string | null | undefined) => {
    if (!value) return '—'
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString('en-GB')
}

/** All client-level reasons for a client, headline first, deduplicated. */
export const clientRejectionReasons = (client: AnalysisClient): string[] => {
    const listed = (client.clientRejectionReasons || []).map((entry) => entry.reason)
    const all = client.clientRejectionReason ? [client.clientRejectionReason, ...listed] : listed
    return all.filter((reason, index) => reason && all.indexOf(reason) === index)
}

/** Reasons beyond the headline one — what the client *also* failed on. */
export const secondaryRejectionReasons = (client: AnalysisClient): string[] =>
    clientRejectionReasons(client).filter((reason) => reason !== (client.clientRejectionReason || 'unspecified'))

/** Details recorded for one reason, whichever slot the analysis stored them in. */
export const rejectionDetailsFor = (client: AnalysisClient, reason: string) => {
    const listed = (client.clientRejectionReasons || []).find((entry) => entry.reason === reason)
    if (listed?.details) return listed.details
    return reason === client.clientRejectionReason ? client.clientRejectionDetails || null : null
}

export interface OverlapEntry {
    key: string
    label: string
    count: number
}

/**
 * Among the clients counted under `reasonKey`, how many also failed each other
 * gate. Sorted by count so the most common overlap is first.
 */
export const buildOverlapBreakdown = (clients: AnalysisClient[], reasonKey: string) => {
    const counts = new Map<string, number>()
    let withOverlaps = 0
    for (const client of clients) {
        if ((client.clientRejectionReason || 'unspecified') !== reasonKey) continue
        const others = secondaryRejectionReasons(client)
        if (others.length) withOverlaps += 1
        for (const reason of others) counts.set(reason, (counts.get(reason) || 0) + 1)
    }
    const entries: OverlapEntry[] = Array.from(counts.entries())
        .map(([key, count]) => ({ key, label: reasonLabel(key), count }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    return { entries, withOverlaps }
}
